import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  MagnifyingGlass,
  Plus,
  Lock,
  CaretDown,
  List,
  Columns,
  ChartBar,
  UploadSimple
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../../context/BusinessContext';
import { useBusinessSubscription } from '../../../hooks/useBusinessSubscription';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import CRMListView, { CRM_GROUP_BY, CRM_LAYOUT } from './CRMListView';
import { loadCrmPreferences, saveCrmPreferencesMerge } from '../../../utils/crmPreferences';
import { normalizeCrmStages } from '../../../utils/crmStagesOrder';
import { mergeLeadSources, getLeadSourceLabel } from '../../../utils/crmLeadSources';
import { fetchAllLeadsPages } from '../../../utils/crmLeadPagination';
import CreateLeadModal from './CreateLeadModal';
import CRMStagesManager from './CRMStagesManager';
import CRMSourcesManager from './CRMSourcesManager';
import CRMImportModal from './CRMImportModal';

const IMPORT_LOCKED_PLANS = ['supplier_free', 'supplier_free_trial'];
const DEFAULT_FILTERS = {
  stage: '',
  assignedTo: '',
  priority: '',
  leadSource: '',
  tags: '',
  hasWedding: '',
  search: '',
  archived: false
};

const MyCouplesDashboard = () => {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const { planKey } = useBusinessSubscription();
  
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(() => {
    const prefs = loadCrmPreferences();
    const savedFilters = prefs?.filters || {};
    return {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      archived: savedFilters?.archived === true || savedFilters?.archived === 'true'
    };
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStageManager, setShowStageManager] = useState(false);
  const [showSourceManager, setShowSourceManager] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportUpgradeDialog, setShowImportUpgradeDialog] = useState(false);
  const [showExportUpgradeDialog, setShowExportUpgradeDialog] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [groupBy, setGroupBy] = useState(() => {
    const p = loadCrmPreferences();
    return Object.values(CRM_GROUP_BY).includes(p.groupBy) ? p.groupBy : CRM_GROUP_BY.STAGE;
  });
  const [crmLayout, setCrmLayout] = useState(() => {
    const p = loadCrmPreferences();
    const g = Object.values(CRM_GROUP_BY).includes(p.groupBy) ? p.groupBy : CRM_GROUP_BY.STAGE;
    let l = Object.values(CRM_LAYOUT).includes(p.layout) ? p.layout : CRM_LAYOUT.LIST;
    if (g !== CRM_GROUP_BY.STAGE && l === CRM_LAYOUT.KANBAN) l = CRM_LAYOUT.LIST;
    return l;
  });
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [leadSources, setLeadSources] = useState(() => mergeLeadSources([]));
  const [_stats, setStats] = useState({
    totalLeads: 0,
    hotLeads: 0,
    followUpsNeeded: 0,
    conversionRate: 0
  });
  const businessId = selectedBusiness?.business?._id;
  const hasInitiallyLoaded = useRef(false);
  const availableTags = useMemo(() => {
    const tags = new Set();
    leads.forEach((lead) => {
      if (!Array.isArray(lead?.tags)) return;
      lead.tags.forEach((tag) => {
        const cleanTag = String(tag || '').trim();
        if (cleanTag) tags.add(cleanTag);
      });
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const refreshStages = async () => {
    if (!businessId) return;
    try {
      const response = await api.get('/api/crm/stages', { params: { businessId } });
      setStages(normalizeCrmStages(response.data?.stages || []));
    } catch (error) {}
  };

  const fetchSources = async () => {
    if (!businessId) return;
    try {
      const response = await api.get('/api/crm/sources', { params: { businessId } });
      setLeadSources(mergeLeadSources(response.data?.sources || []));
    } catch (error) {
      setLeadSources(mergeLeadSources([]));
    }
  };

  useEffect(() => {
    if (selectedBusiness) {
      fetchLeads();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness, filters]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchSources();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness]);

  useEffect(() => {
    if (groupBy !== CRM_GROUP_BY.STAGE && crmLayout === CRM_LAYOUT.KANBAN) {
      setCrmLayout(CRM_LAYOUT.LIST);
    }
  }, [groupBy, crmLayout]);

  useEffect(() => {
    saveCrmPreferencesMerge({ groupBy, layout: crmLayout });
  }, [groupBy, crmLayout]);

  useEffect(() => {
    saveCrmPreferencesMerge({ filters });
  }, [filters]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      // Map new priority values to old values for backend compatibility
      const priorityMap = {
        'high': 'hot',
        'medium': 'warm',
        'low': 'cold'
      };

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          // Convert priority value if it's the priority filter
          if (key === 'priority' && priorityMap[value]) {
            queryParams.append(key, priorityMap[value]);
          } else {
            queryParams.append(key, value);
          }
        }
      });

      // Pass businessId if available
      if (selectedBusiness?.business?._id) {
        queryParams.append('businessId', selectedBusiness.business._id);
      }
      if (filters.archived) {
        queryParams.append('archived', 'true');
      }

      const data = await fetchAllLeadsPages(api, queryParams);
      setStages(normalizeCrmStages(data.stages || []));

      // Fetch date warnings for leads with weddings
      const leadsWithWarnings = await Promise.all(
        (data.leads || []).map(async (lead) => {
          if (lead.wedding && lead._id) {
            try {
              const warningsResponse = await api.get(`/api/leads/${lead._id}/date-warnings`);
              return {
                ...lead,
                hasDateWarning: warningsResponse.data.hasWarnings
              };
            } catch (error) {
              return lead;
            }
          }
          return lead;
        })
      );

      setLeads(leadsWithWarnings);
    } catch (err) {
      setError(err.message || 'Failed to load your list');
    } finally {
      setLoading(false);
      hasInitiallyLoaded.current = true;
    }
  };

  const fetchStats = async () => {
    try {
      // Pass businessId if available
      const params = {};
      if (selectedBusiness?.business?._id) {
        params.businessId = selectedBusiness.business._id;
      }
      const response = await api.get('/api/crm/analytics', { params });
      const data = response.data;
      setStats({
        totalLeads: data.totalLeads || 0,
        hotLeads: data.leadsByPriority?.find(p => p._id === 'high' || p._id === 'hot')?.count || 0,
        followUpsNeeded: data.upcomingReminders || 0,
        conversionRate: data.conversionRate || 0
      });
    } catch (err) {}
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleCreateLead = () => {
    setShowCreateModal(true);
  };

  const handleLeadClick = (lead) => {
    navigate(`/supplier/crm/leads/${lead._id}`);
  };

  const handleLeadCreated = () => {
    setShowCreateModal(false);
    fetchLeads();
    fetchStats();
  };

  const handleImportComplete = () => {
    setShowImportModal(false);
    fetchLeads();
    fetchStats();
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await api.put(`/api/leads/${leadId}/stage`, { stage: newStage });
      const stageMeta = stages.find(s => s.slug === newStage);
      setLeads(prev =>
        prev.map(lead =>
          lead._id === leadId
            ? {
                ...lead,
                stage: newStage,
                stageSlug: newStage,
                stageName: stageMeta?.name || newStage,
                stageType: stageMeta?.type
              }
            : lead
        )
      );
      fetchStats();
    } catch (err) {}
  };

  const _handleAssignLead = async (leadId, assignedTo) => {
    try {
      const _response = await api.put(`/api/leads/${leadId}/assign`, { assignedTo });
      fetchLeads();
    } catch (err) {}
  };

  const handleStagesUpdated = async () => {
    await refreshStages();
    fetchLeads();
  };

  const handleSourcesUpdated = async () => {
    await fetchSources();
    fetchLeads();
  };

  const handleEditStage = () => {
    setShowStageManager(true);
  };

  const closeActionsMenu = () => setActionsMenuAnchor(null);
  const closeFilterMenu = () => setFilterMenuAnchor(null);
  const closeGroupMenu = () => setGroupMenuAnchor(null);
  const activeFilterCount = [
    filters.search,
    filters.stage,
    filters.assignedTo,
    filters.priority,
    filters.leadSource,
    filters.tags,
    filters.hasWedding,
    filters.archived ? 'archived' : ''
  ].filter((value) => Boolean(String(value || '').trim())).length;

  const handleActionsImportCouples = () => {
    closeActionsMenu();
    if (IMPORT_LOCKED_PLANS.includes(planKey)) {
      setShowImportUpgradeDialog(true);
    } else {
      setShowImportModal(true);
    }
  };

  const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const formatAddedDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB');
  };

  const handleExportCouplesCsv = () => {
    closeActionsMenu();
    if (IMPORT_LOCKED_PLANS.includes(planKey)) {
      setShowExportUpgradeDialog(true);
      return;
    }
    if (!leads.length) return;

    const csvRows = [
      'Name,Email,Added',
      ...leads.map((lead) => `${escapeCsvValue(lead?.name)},${escapeCsvValue(lead?.email)},${escapeCsvValue(formatAddedDate(lead?.createdAt))}`)
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.setAttribute('download', `couples-export-${dateStamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !hasInitiallyLoaded.current) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box
          display="flex"
          flexDirection={{ xs: 'row', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'center', sm: 'center' }}
          sx={{ mb: 3, gap: { xs: 2, sm: 0 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              My Couples
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' } }}
            >
              Track every couple and stage in one place
            </Typography>
          </Box>
          {/* Desktop: Analytics, Import, Add couple */}
          <Box display={{ xs: 'none', md: 'flex' }} gap={2}>
            <Button variant="outlined" startIcon={<ChartBar size={20} />} onClick={() => navigate('/supplier/crm/analytics')} size="medium">
              Analytics
            </Button>
            {IMPORT_LOCKED_PLANS.includes(planKey) ? (
              <Button variant="outlined" startIcon={<Lock size={20} />} onClick={() => setShowImportUpgradeDialog(true)} size="medium">
                Import
              </Button>
            ) : (
              <Button variant="outlined" startIcon={<UploadSimple size={20} />} onClick={() => setShowImportModal(true)} size="medium">
                Import
              </Button>
            )}
            <Button variant="contained" startIcon={<Plus size={20} />} onClick={handleCreateLead} size="medium">
              Add couple
            </Button>
          </Box>
          {/* Mobile: add couple (icon only) */}
          <IconButton
            onClick={handleCreateLead}
            aria-label="Add couple"
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexShrink: 0,
              width: 48,
              height: 48,
              backgroundColor: '#000',
              color: '#fff',
              '&:hover': { backgroundColor: '#333' }
            }}
          >
            <Plus size={24} weight="bold" />
          </IconButton>
        </Box>

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          sx={{ mb: 2, width: '100%', justifyContent: 'flex-start' }}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleCreateLead}
            sx={{ textTransform: 'none' }}
          >
            Add couple
          </Button>
          <Button
            size="small"
            variant={activeFilterCount ? 'contained' : 'outlined'}
            endIcon={<CaretDown size={16} weight="bold" />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(filterMenuAnchor)}
            sx={{ textTransform: 'none' }}
          >
            {activeFilterCount ? `Filter (${activeFilterCount})` : 'Filter'}
          </Button>
          <Popover
            open={Boolean(filterMenuAnchor)}
            anchorEl={filterMenuAnchor}
            onClose={closeFilterMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: { p: 2, width: { xs: 'min(100vw - 32px, 360px)', sm: 360 }, maxWidth: '100%' }
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Filter list
              </Typography>
              <Button
                size="small"
                onClick={handleResetFilters}
                disabled={!activeFilterCount}
                sx={{ textTransform: 'none' }}
              >
                Reset filters
              </Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or email…"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlass size={18} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Stage</InputLabel>
                  <Select
                    value={filters.stage || ''}
                    onChange={(e) => handleFilterChange('stage', e.target.value)}
                    label="Stage"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === '') return 'All stages';
                      const s = stages.find((st) => st.slug === selected);
                      return s ? s.name : selected;
                    }}
                  >
                    <MenuItem value="">All stages</MenuItem>
                    {stages
                      .filter((s) => !s.isArchived)
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((s) => (
                        <MenuItem key={s.slug} value={s.slug}>
                          {s.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Priority</InputLabel>
                  <Select
                    value={filters.priority || ''}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    label="Priority"
                    displayEmpty
                    renderValue={(s) =>
                      !s ? 'All priorities' : { high: 'High', medium: 'Medium', low: 'Low' }[s] || s
                    }
                  >
                    <MenuItem value="">All priorities</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Source</InputLabel>
                  <Select
                    value={filters.leadSource || ''}
                    onChange={(e) => handleFilterChange('leadSource', e.target.value)}
                    label="Source"
                    displayEmpty
                    renderValue={(s) => (!s ? 'All sources' : getLeadSourceLabel(s, leadSources))}
                  >
                    <MenuItem value="">All sources</MenuItem>
                    {leadSources.map((source) => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Tag</InputLabel>
                  <Select
                    value={filters.tags || ''}
                    onChange={(e) => handleFilterChange('tags', e.target.value)}
                    label="Tag"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === '') return 'All tags';
                      return selected;
                    }}
                  >
                    <MenuItem value="">All tags</MenuItem>
                    {availableTags.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Popover>

          <Button
            size="small"
            variant="outlined"
            endIcon={<CaretDown size={16} weight="bold" />}
            onClick={(e) => setGroupMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(groupMenuAnchor)}
            sx={{ textTransform: 'none' }}
          >
            Group by
          </Button>
          <Menu
            anchorEl={groupMenuAnchor}
            open={Boolean(groupMenuAnchor)}
            onClose={closeGroupMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            <MenuItem
              selected={groupBy === CRM_GROUP_BY.STAGE}
              onClick={() => {
                setGroupBy(CRM_GROUP_BY.STAGE);
                closeGroupMenu();
              }}
            >
              Stage
            </MenuItem>
            <MenuItem
              selected={groupBy === CRM_GROUP_BY.SOURCE}
              onClick={() => {
                setGroupBy(CRM_GROUP_BY.SOURCE);
                closeGroupMenu();
              }}
            >
              Source
            </MenuItem>
            <MenuItem
              selected={groupBy === CRM_GROUP_BY.PRIORITY}
              onClick={() => {
                setGroupBy(CRM_GROUP_BY.PRIORITY);
                closeGroupMenu();
              }}
            >
              Priority
            </MenuItem>
            <MenuItem
              selected={groupBy === CRM_GROUP_BY.ASSIGNEE}
              onClick={() => {
                setGroupBy(CRM_GROUP_BY.ASSIGNEE);
                closeGroupMenu();
              }}
            >
              Assignee
            </MenuItem>
            <MenuItem
              selected={groupBy === CRM_GROUP_BY.TAG}
              onClick={() => {
                setGroupBy(CRM_GROUP_BY.TAG);
                closeGroupMenu();
              }}
            >
              Tag
            </MenuItem>
          </Menu>

          <Button
            size="small"
            variant="outlined"
            endIcon={<CaretDown size={16} weight="bold" />}
            onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(actionsMenuAnchor)}
            aria-controls={actionsMenuAnchor ? 'crm-actions-menu' : undefined}
            sx={{ textTransform: 'none' }}
          >
            Actions
          </Button>
          <Menu
            id="crm-actions-menu"
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={closeActionsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            <MenuItem
              disabled={!businessId}
              onClick={() => {
                setShowStageManager(true);
                closeActionsMenu();
              }}
            >
              Manage Stages
            </MenuItem>
            <MenuItem onClick={handleActionsImportCouples}>
              {IMPORT_LOCKED_PLANS.includes(planKey) ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <Lock size={18} />
                  Import Couples
                </Box>
              ) : (
                'Import Couples'
              )}
            </MenuItem>
            <MenuItem disabled={!leads.length} onClick={handleExportCouplesCsv}>
              {IMPORT_LOCKED_PLANS.includes(planKey) ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <Lock size={18} />
                  Export couples (CSV)
                </Box>
              ) : (
                'Export couples (CSV)'
              )}
            </MenuItem>
            <MenuItem
              disabled={!businessId}
              onClick={() => {
                setShowSourceManager(true);
                closeActionsMenu();
              }}
            >
              Manage Sources
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/supplier/crm/analytics');
                closeActionsMenu();
              }}
            >
              View analytics
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleFilterChange('archived', !filters.archived);
                closeActionsMenu();
              }}
            >
              {filters.archived ? 'View active couples' : 'View archive'}
            </MenuItem>
          </Menu>

          <Box sx={{ flexGrow: 1, minWidth: { xs: 0, sm: 8 } }} />

          <Tooltip
            title={
              groupBy !== CRM_GROUP_BY.STAGE
                ? 'Kanban is available when grouping by stage'
                : 'Switch between list and Kanban'
            }
          >
            <ToggleButtonGroup
              value={crmLayout}
              exclusive
              onChange={(_, value) => {
                if (value !== null) setCrmLayout(value);
              }}
              size="small"
              aria-label="My Couples view layout"
              sx={{
                flexShrink: 0,
                borderRadius: 0.5,
                overflow: 'hidden',
                '& .MuiToggleButton-root': {
                  py: 0.5,
                  px: 1
                }
              }}
            >
              <ToggleButton value={CRM_LAYOUT.LIST} aria-label="List view">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <List size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    List
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value={CRM_LAYOUT.KANBAN} disabled={groupBy !== CRM_GROUP_BY.STAGE} aria-label="Kanban view">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Columns size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Kanban
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        </Stack>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <CRMListView
        stages={stages}
        leads={leads}
        leadSources={leadSources}
        onStageChange={handleStageChange}
        onLeadClick={handleLeadClick}
        onEditStage={handleEditStage}
        loading={loading}
        viewingArchived={filters.archived}
        groupBy={groupBy}
        layout={crmLayout}
      />

      {/* Create Lead Modal */}
      <CreateLeadModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        leadSources={leadSources}
        availableTags={availableTags}
        onLeadCreated={handleLeadCreated}
      />

      {/* Import Modal */}
      <CRMImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        businessId={businessId}
        onImported={handleImportComplete}
      />

      {/* Import upgrade dialog (Free / Free trial) */}
      <Dialog open={showImportUpgradeDialog} onClose={() => setShowImportUpgradeDialog(false)}>
        <DialogTitle>Import locked</DialogTitle>
        <DialogContent>
          <Typography>
            To import couples from a spreadsheet, upgrade to an Individual or Team account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImportUpgradeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setShowImportUpgradeDialog(false); navigate('/supplier/billing'); }}>
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showExportUpgradeDialog} onClose={() => setShowExportUpgradeDialog(false)}>
        <DialogTitle>Export locked</DialogTitle>
        <DialogContent>
          <Typography>
            To export couples as a CSV file, upgrade to an Individual or Team account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportUpgradeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setShowExportUpgradeDialog(false); navigate('/supplier/billing'); }}>
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      <CRMStagesManager
        open={showStageManager}
        onClose={() => setShowStageManager(false)}
        stages={stages}
        businessId={businessId}
        onStagesUpdated={handleStagesUpdated}
      />
      <CRMSourcesManager
        open={showSourceManager}
        onClose={() => setShowSourceManager(false)}
        sources={leadSources}
        businessId={businessId}
        onSourcesUpdated={handleSourcesUpdated}
      />
    </Box>
  );
};

export default MyCouplesDashboard;
